import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { EnvTypes, flowMapper } from '@shared/models';
import { LoggerService } from '@shared/modules/logger/logger';
import { TrackProcessStep } from '@store/actions/adobe-launch/adobe-launch.actions';
import * as fromRootStore from '@store/index';
import {
  GetAndSetAllFeatureToggles,
  LoadContent,
  LoadFMFScript,
  SetInfoForSurvey,
} from '@store/index';
import { selectTriageState } from '@store/selectors/triage/triage.selectors';
import { filter, map, Subscription, withLatestFrom } from 'rxjs';
import { VgimAdobeLaunchService } from 'vg-interaction-model';

import { getConfigClientFromRTE } from '../../config/config-client';

declare global {
  interface Window {
    Cypress: any;
    store: any;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  subs: Subscription = new Subscription();
  env!: EnvTypes;

  constructor(
    private globalStore: Store<fromRootStore.State>,
    private router: Router,
    private adobeLaunch: VgimAdobeLaunchService,
    private logger: LoggerService,
  ) {}

  ngOnInit(): void {
    if (window.Cypress) {
      // Make sure this matches the injected store name. If there is no injected store name in the constructor, you will have to add it
      window.store = this.globalStore;
    }

    this.globalStore.dispatch(LoadContent());
    this.globalStore.dispatch(SetInfoForSurvey());
    this.globalStore.dispatch(GetAndSetAllFeatureToggles());

    // TODO - figure out if something needs to happen here, or this just depended on internal/external
    this.globalStore.dispatch(LoadFMFScript());

    this.env = getConfigClientFromRTE().isProduction ? EnvTypes.PRODUCTION : EnvTypes.DEVELOPMENT;

    this.adobeLaunch.handleAppInit({ pageTemplate: 'CWP' }, this.env, this.router, {
      countryCode: 'us',
      language: 'en',
      siteDescriptor: 'retail',
      platform: 'web',
    });

    this.subs.add(
      this.adobeLaunch.errors.subscribe((err: Error) => {
        this.logger.error({ message: err.message, eventId: 'adobe-launch-failure' }, 0);
      }),
    );
    this.subs.add(
      this.router.events
        .pipe(
          filter((event: any) => event instanceof NavigationEnd),
          map(() => this.router.routerState.snapshot.root),
          map((route) => {
            while (route.firstChild) {
              route = route.firstChild;
            }
            return route;
          }),
          withLatestFrom(this.globalStore.pipe(select(selectTriageState))),
        )
        .subscribe(([route, triageState]) => {
          const pageId = route.data.pageId;
          const routeFlow = route.data.flow;
          const showAcctType = route.data.accountType;
          const accountType = triageState.accountTypeInheriting;

          const flowType = triageState.flowType ? flowMapper[triageState.flowType] : routeFlow;
          const data = {
            processStep: `coo-${flowType}-${pageId}`,
            processType: `coo-${flowType}-flow`,
            ...(showAcctType && { accountType: accountType }),
          };
          this.globalStore.dispatch(TrackProcessStep(data));
        }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
